/*
 * Copyright © HatioLab Inc. All rights reserved.
 */
import { Component, Properties, ComponentNature, RectPath, Shape, sceneComponent, RealObject } from '@hatiolab/things-scene'
import * as THREE from 'three'

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'number',
      label: 'shelves',
      name: 'shelves',
      property: 'shelves'
    },
    {
      type: 'color',
      label: 'shelf-color',
      name: 'shelfColor',
      property: 'shelfColor'
    },
    {
      type: 'color',
      label: 'post-color',
      name: 'postColor',
      property: 'postColor'
    },
    {
      type: 'id-input',
      label: 'host',
      name: 'host',
      property: {
        component: true
      }
    }
  ],
  help: 'scene/component/carrier'
}

const ANIM_DURATION = 1000

interface AnimTarget {
  left: number
  top: number
  zPos: number
  rotation: number
}

interface AnimState {
  startTime: number
  from: AnimTarget
  to: AnimTarget
}

const DEFAULT_SHELF_COLOR = '#b0b0b0'
const DEFAULT_POST_COLOR = '#666666'

export class Carrier3D extends RealObject {
  build() {
    super.build()

    var { width, height, depth, fillStyle, shelfColor, postColor, shelves = 2 } = this.component.state

    var w = width
    var h = height
    var d = depth || 60
    var numShelves = Math.max(1, Math.min(shelves, 5))

    var shelfThick = Math.max(1.5, d * 0.04)
    var postR = Math.min(w, h) * 0.03
    var totalH = d

    var shelfMat = new THREE.MeshStandardMaterial({
      color: shelfColor || fillStyle || DEFAULT_SHELF_COLOR,
      roughness: 0.4,
      metalness: 0.2
    })
    var postMat = new THREE.MeshStandardMaterial({
      color: postColor || DEFAULT_POST_COLOR,
      roughness: 0.5,
      metalness: 0.3
    })

    // ── 선반판 ──
    for (var i = 0; i < numShelves; i++) {
      var shelfY = (i / (numShelves - 1 || 1)) * (totalH - shelfThick)
      var shelfGeo = new THREE.BoxGeometry(w, shelfThick, h)
      var shelf = new THREE.Mesh(shelfGeo, shelfMat)
      shelf.position.y = shelfY + shelfThick / 2
      shelf.castShadow = true
      shelf.receiveShadow = true
      this.object3d.add(shelf)
    }

    // ── 기둥 4개 (모서리) ──
    var postGeo = new THREE.CylinderGeometry(postR, postR, totalH, 8)
    var insetX = w * 0.42
    var insetZ = h * 0.42

    for (var px of [-insetX, insetX]) {
      for (var pz of [-insetZ, insetZ]) {
        var post = new THREE.Mesh(postGeo, postMat)
        post.position.set(px, totalH / 2, pz)
        post.castShadow = true
        this.object3d.add(post)
      }
    }

    // 수직 중앙 정렬
    this.object3d.children.forEach(child => {
      child.position.y -= totalH / 2
    })
  }
}

/* ── 2D 렌더 ── */

function renderCarrier(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fillStyle: string,
  shelfColor: string,
  postColor: string,
  shelves: number
) {
  var numShelves = Math.max(1, Math.min(shelves || 2, 5))
  var postW = Math.max(2, w * 0.06)
  var inset = postW / 2

  // ── 기둥 4개 ──
  ctx.fillStyle = postColor || DEFAULT_POST_COLOR
  for (var px of [x + inset, x + w - inset - postW]) {
    for (var py of [y + inset, y + h - inset - postW]) {
      ctx.fillRect(px, py, postW, postW)
    }
  }

  // ── 선반판 ──
  ctx.fillStyle = shelfColor || fillStyle || DEFAULT_SHELF_COLOR
  var shelfH = Math.max(2, h * 0.04)
  for (var i = 0; i < numShelves; i++) {
    var sy = y + (i / (numShelves - 1 || 1)) * (h - shelfH)
    ctx.fillRect(x, sy, w, shelfH)
  }

  // 외곽선
  ctx.strokeStyle = '#999999'
  ctx.lineWidth = 1
  ctx.strokeRect(x, y, w, h)
}

@sceneComponent('carrier')
export class Carrier extends RectPath(Shape) {
  private _host?: Component
  private _syncingToHost = false
  private _animState?: AnimState
  private _animFrameId = 0
  buildRealObject(): RealObject | undefined {
    return new Carrier3D(this as any)
  }

  get nature() {
    return NATURE
  }

  get hostComponent(): Component | undefined {
    var { host } = this.state

    if (!this._host && host) {
      this._host = this.root.findById?.(host)
      this._host?.on('change', this._onHostChanged, this)
    }

    return this._host
  }

  private _getHostTarget(): AnimTarget | undefined {
    var host = this._host
    if (!host) return undefined

    var hs = host.state
    var { width, height } = this.state

    return {
      left: hs.left + (hs.width - width) / 2,
      top: hs.top + (hs.height - height) / 2,
      zPos: (hs.zPos || 0) + (hs.depth || 0),
      rotation: hs.rotation || 0
    }
  }

  private _onHostChanged(after: Properties) {
    if (
      after.hasOwnProperty('left') ||
      after.hasOwnProperty('top') ||
      after.hasOwnProperty('zPos') ||
      after.hasOwnProperty('width') ||
      after.hasOwnProperty('height') ||
      after.hasOwnProperty('depth') ||
      after.hasOwnProperty('rotation')
    ) {
      if (this._animState) {
        // 애니메이션 중이면 목표만 갱신
        var target = this._getHostTarget()
        if (target) this._animState.to = target
      } else {
        this._syncToHost()
      }
    }
  }

  private _syncToHost() {
    var target = this._getHostTarget()
    if (!target) return

    this._syncingToHost = true
    this.set(target)
    this._syncingToHost = false
  }

  private _animateToHost() {
    var target = this._getHostTarget()
    if (!target) return

    this._cancelAnimation()

    var { left, top, rotation = 0 } = this.state
    var zPos = this.state.zPos || 0

    this._animState = {
      startTime: Date.now(),
      from: { left, top, zPos, rotation },
      to: target
    }

    this._animStep()
  }

  private _animStep() {
    var anim = this._animState
    if (!anim) return

    var t = Math.min((Date.now() - anim.startTime) / ANIM_DURATION, 1)

    // easeInOutCubic
    var ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

    this._syncingToHost = true
    this.set({
      left: anim.from.left + (anim.to.left - anim.from.left) * ease,
      top: anim.from.top + (anim.to.top - anim.from.top) * ease,
      zPos: anim.from.zPos + (anim.to.zPos - anim.from.zPos) * ease,
      rotation: anim.from.rotation + (anim.to.rotation - anim.from.rotation) * ease
    })
    this._syncingToHost = false

    if (t < 1) {
      this._animFrameId = requestAnimationFrame(() => this._animStep())
    } else {
      this._animState = undefined
      this._animFrameId = 0
    }
  }

  private _cancelAnimation() {
    if (this._animFrameId) {
      cancelAnimationFrame(this._animFrameId)
      this._animFrameId = 0
    }
    this._animState = undefined
  }

  private _unbindHost() {
    this._cancelAnimation()
    if (this._host) {
      this._host.off('change', this._onHostChanged, this)
      delete this._host
    }
  }

  onchange(after: Properties, before: Properties) {
    if (after.hasOwnProperty('host') || before.hasOwnProperty('host')) {
      this._unbindHost()
      // 새 호스트로 애니메이션 전환
      if (this.hostComponent) {
        this._animateToHost()
      }
    }

    // 호스트에 의한 위치 동기화 중이면 재귀 방지
    if (!this._syncingToHost && this._host && !this._animState) {
      if (
        after.hasOwnProperty('width') ||
        after.hasOwnProperty('height')
      ) {
        this._syncToHost()
      }
    }

    super.onchange(after, before)
  }

  async ready() {
    // 초기 로드 시 즉시 바인딩 (애니메이션 없이)
    if (this.hostComponent) {
      this._syncToHost()
    }
  }

  dispose() {
    this._unbindHost()
    super.dispose()
  }

  render(ctx: CanvasRenderingContext2D) {
    var { left, top, width, height } = this.bounds
    var fillStyle = this.getState('fillStyle')
    var shelfColor = this.getState('shelfColor')
    var postColor = this.getState('postColor')
    var shelves = this.getState('shelves')

    renderCarrier(ctx, left, top, width, height, fillStyle, shelfColor, postColor, shelves)

    ctx.beginPath()
    ctx.rect(left, top, width, height)
  }
}
